/**
 * Single source of truth for site-wide and per-route metadata.
 *
 * Domains are deliberately split:
 *   - the website is yunlen.com  (canonical links, og:url, sitemap)
 *   - email is @yunlen.app       (support@, privacy@, legal@, contact@, ...)
 * Do not "fix" one to match the other.
 */
export const SITE_URL = 'https://yunlen.com';
export const SITE_NAME = 'YUNLEN';
export const TWITTER_HANDLE = '@yunlenapp';

export interface RouteMeta {
    title: string;
    description: string;
    /** Path under /og, without extension. */
    image: string;
    /** Omit from the sitemap (legal boilerplate, utility pages). */
    lowPriority?: boolean;
    /** Never index (thin, duplicated or private). */
    noindex?: boolean;
}

const DEFAULT_IMAGE = 'og-home';

/**
 * Titles are written for search results: the distinguishing phrase first,
 * brand last, under ~60 characters. Descriptions are 140–160 characters and
 * describe the page rather than repeating the brand boilerplate.
 */
export const ROUTE_META: Record<string, RouteMeta> = {
    '/': {
        title: 'Yunlen — Global Finance Simplified',
        description:
            'Multi-currency accounts in USD, GBP, EUR and AED, instant cross-border transfers, virtual Visa cards and stablecoin wallets. Transparent pricing, no hidden fees.',
        image: 'og-home',
    },
    '/accounts': {
        title: 'USD, GBP, EUR & AED Accounts | Yunlen',
        description:
            'Get local account details in four currencies: a US routing and account number, a UK sort code, a European IBAN and a UAE account. Receive over ACH, wire, Faster Payments and SEPA Instant.',
        image: 'og-accounts',
    },
    '/cards': {
        title: 'Virtual & Physical Visa Cards | Yunlen',
        description:
            'Issue a virtual Visa card in seconds or order a physical one. Set limits, freeze and unfreeze instantly, and pay with Apple Pay and Google Pay wherever Visa is accepted.',
        image: 'og-cards',
    },
    '/stablecoin-wallet': {
        title: 'Stablecoin Wallet — USDC & USDT | Yunlen',
        description:
            'Hold, send and bridge USDC and USDT across Ethereum, Polygon, Solana, Optimism and Avalanche. Cross-chain transfers with low fees and settlement in seconds.',
        image: 'og-wallet',
    },
    '/about': {
        title: 'About Yunlen',
        description:
            'Yunlen builds virtual payment accounts for the world — multi-currency banking, cards and stablecoin settlement for freelancers, remote workers and global businesses.',
        image: DEFAULT_IMAGE,
    },
    '/blog': {
        title: 'Blog | Yunlen',
        description:
            'Guides and updates on cross-border payments, multi-currency accounts, virtual cards and stablecoins from the Yunlen team.',
        image: DEFAULT_IMAGE,
    },
    '/faq': {
        title: 'Frequently Asked Questions | Yunlen',
        description:
            'Answers on opening an account, supported countries and currencies, card issuing, transfer times, fees, limits and stablecoin settlement.',
        image: DEFAULT_IMAGE,
    },
    '/contact': {
        title: 'Contact Yunlen',
        description:
            'Get in touch with the Yunlen team about accounts, cards, transfers or partnerships. We typically respond within a few hours.',
        image: DEFAULT_IMAGE,
    },
    '/downloadapp': {
        title: 'Download the Yunlen App — iOS & Android',
        description:
            'Get Yunlen on iPhone and Android. Open a multi-currency account, issue a Visa card and move money across 20+ countries from your phone.',
        image: DEFAULT_IMAGE,
    },
    '/terms': {
        title: 'Terms of Service | Yunlen',
        description: 'The terms governing your use of Yunlen accounts, cards and wallet services.',
        image: DEFAULT_IMAGE,
        lowPriority: true,
    },
    '/privacy': {
        title: 'Privacy Policy | Yunlen',
        description: 'How Yunlen collects, uses, stores and protects your personal and financial data.',
        image: DEFAULT_IMAGE,
        lowPriority: true,
    },
    '/cards/cardpolicy': {
        title: 'Card Policy | Yunlen',
        description: 'Terms and conditions covering Yunlen virtual and physical Visa cards.',
        image: 'og-cards',
        lowPriority: true,
    },
    '/cards/prohibited-activities': {
        title: 'Prohibited Activities | Yunlen',
        description: 'Merchant categories and activities that cannot be funded with a Yunlen card.',
        image: 'og-cards',
        lowPriority: true,
    },
    '/cards/esign': {
        title: 'E-Sign Notice | Yunlen',
        description: 'Consent to receive Yunlen disclosures and records electronically.',
        image: 'og-cards',
        lowPriority: true,
    },
    '/requestremoval': {
        title: 'Request Data Removal | Yunlen',
        description: 'Request removal of your personal data from Yunlen systems.',
        image: DEFAULT_IMAGE,
        noindex: true,
    },
};

/** Routes that resolve to the same page and must not compete in search. */
export const DUPLICATE_ROUTES = [
    '/download',
    '/download-app',
    '/download-apps',
    '/downloadpage',
    '/dowloadapp',
    '/downloadap',
    '/usd-bank-account',
    '/eur-bank-account',
];

export const FALLBACK_META: RouteMeta = {
    title: 'Yunlen — Global Finance Simplified',
    description:
        'Multi-currency accounts, instant cross-border transfers, virtual Visa cards and stablecoin wallets. Transparent pricing, no hidden fees.',
    image: DEFAULT_IMAGE,
};

export const getRouteMeta = (pathname: string): RouteMeta => {
    const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    if (ROUTE_META[clean]) return ROUTE_META[clean];
    // Blog posts inherit the blog card until a post-specific one is set.
    if (clean.startsWith('/blog/')) {
        return { ...ROUTE_META['/blog'], title: 'Article | Yunlen Blog' };
    }
    if (clean.startsWith('/sp')) {
        return { ...FALLBACK_META, title: 'Service Provider Disclosures | Yunlen', lowPriority: true };
    }
    return FALLBACK_META;
};
